import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Checkbox,
  Collapse,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  Stack,
  Typography,
  styled,
  useTheme,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { Button } from "Components/_Shared/Buttons/Button";
import { DialogModal } from "Components/_Shared/DialogModal";
import { ReactComponent as AddIcon } from "Components/_Shared/Icons/Assets/add.svg";
import { ReactComponent as BinIcon } from "Components/_Shared/Icons/Assets/bin.svg";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { FormDropdown, type Option } from "Forms/Components/FormDropdown";
import { StyledLabelAdornment } from "Forms/Components/FormStyles";
import {
  type InsuranceExpenseCustomItem,
  type InsuranceExpenseFormType,
  getInsuranceExpenseFormSchema,
} from "Forms/Schemas/Budget/Expenses/insuranceExpenseFormSchema";
import {
  getGetBudgetOverviewQueryKey,
  getGetInsuranceExpenseQueryKey,
  useCreateInsuranceExpenseItem,
  useDeleteInsuranceExpenseItem,
  useGetAnalysisClients,
  useGetInsuranceExpense,
  useUpdateInsuranceExpenseItem,
  useUpdateInsuranceExpenseSettings,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { AmountFrequency, type InsuranceExpenseItemBody, InsuranceExpenseItemInsuranceType } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatClientName } from "Infrastructure/Utils/ClientUtils";
import { Fragment, type RefObject, forwardRef, useContext, useEffect, useImperativeHandle, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import type { ExpenseFormHandle } from "../Components/EditExpenseDrawer";
import RotatingArrowButton from "../Components/RotatingArrowButton";
import { calculateMonthlyTotal } from "./insuranceMonthlyTotal";

type Props = {
  onClose: () => void;
  ref?: RefObject<ExpenseFormHandle>;
};

type deleteExpenseRequest = {
  expenseItemID: string;
};

const emptyCustomItem: InsuranceExpenseCustomItem = {
  expenseItemID: null,
  type: InsuranceExpenseItemInsuranceType.life_insurance,
  amount: "",
  institution: "",
  belongsTo: "",
  period: AmountFrequency.monthly,
  id: "",
};

export const InsuranceExpenseForm = forwardRef<ExpenseFormHandle, Props>(({ onClose }, ref) => {
  const { readOnly } = useContext(AnalysisDetailContext);
  const { t } = useResource();
  const theme = useTheme();
  const [detailed, setDetailed] = useState(true);
  const [openDeleteModal, setOpenDeleteModal] = useState<deleteExpenseRequest | null>(null);
  const [openDeleteBulkModal, setOpenDeleteBulkModal] = useState<boolean>(false);
  const { analysisId, versionId } = useParams() as {
    analysisId: string;
    versionId: string;
  };

  const queryClient = useQueryClient();
  const { data: insuranceExpense } = useGetInsuranceExpense(analysisId, versionId);
  const { data: clientsData } = useGetAnalysisClients(analysisId, versionId);
  const { mutateAsync: createInsuranceExpenseItemAsync } = useCreateInsuranceExpenseItem();
  const { mutateAsync: updateInsuranceExpenseItemAsync } = useUpdateInsuranceExpenseItem();
  const { mutate: deleteInsuranceExpenseItem, mutateAsync: deleteInsuranceExpenseItemAsync } = useDeleteInsuranceExpenseItem();
  const { mutateAsync: updateInsuranceExpenseSettingsAsync } = useUpdateInsuranceExpenseSettings();

  const belongsToOptions: Option[] = [
    { name: "-", value: "" },
    ...(clientsData?.clients ?? []).map((c) => ({
      name: formatClientName(c.firstName, c.surname, c.birthDate ?? null),
      value: c.guid,
    })),
  ];
  const insuranceTypeOptions: Option[] = [
    {
      name: t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.InsuranceExpenseTypes.LifeInsurance),
      value: InsuranceExpenseItemInsuranceType.life_insurance,
    },
    {
      name: t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.InsuranceExpenseTypes.VehicleInsurance),
      value: InsuranceExpenseItemInsuranceType.vehicle_insurance,
    },
    {
      name: t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.InsuranceExpenseTypes.AccidentInsurance),
      value: InsuranceExpenseItemInsuranceType.accident_insurance,
    },
    {
      name: t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.InsuranceExpenseTypes.HouseInsurance),
      value: InsuranceExpenseItemInsuranceType.house_insurance,
    },
    {
      name: t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.InsuranceExpenseTypes.Other),
      value: InsuranceExpenseItemInsuranceType.other,
    },
  ];

  const frequencyOptions: Option[] = [
    { name: t(Resources.Common.Monthly), value: AmountFrequency.monthly },
    { name: t(Resources.Common.Quarterly), value: AmountFrequency.quarterly },
    { name: t(Resources.Common.HalfYearly), value: AmountFrequency.half_yearly },
    { name: t(Resources.Common.Yearly), value: AmountFrequency.yearly },
  ];

  const {
    control,
    setValue,
    watch,
    formState: { errors, dirtyFields },
    handleSubmit,
    clearErrors,
  } = useForm<InsuranceExpenseFormType>({
    resolver: yupResolver(getInsuranceExpenseFormSchema(t)),
    mode: "onBlur",
    defaultValues: {
      aggregatedAmount: "",
      customItems: [],
      detailed: true,
    },
  });

  const {
    fields: customFields,
    append,
    remove,
    replace,
  } = useFieldArray({
    control,
    name: "customItems",
  });

  const customItems = watch("customItems");

  useEffect(() => {
    if (detailed && customItems) {
      setValue("aggregatedAmount", calculateMonthlyTotal(customItems).toString());
    }
  }, [customItems, detailed, setValue]);

  const recalculateTotal = () => {
    if (detailed && customItems) {
      setValue("aggregatedAmount", calculateMonthlyTotal(customItems).toString());
    }
  };

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      handleSubmit(onSubmit)();
    },
    deleteExpense: () => {
      setOpenDeleteBulkModal(true);
    },
  }));

  const [expandedMap, setExpandedMap] = useState<Record<string, boolean>>({});
  const toggle = (id: string) => {
    setExpandedMap((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  useEffect(() => {
    if (insuranceExpense) {
      handleChangeDetailed(insuranceExpense.entryMode === "individual");
      setValue("aggregatedAmount", (insuranceExpense.aggregatedAmount?.value ?? 0).toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insuranceExpense, setValue]);

  useEffect(() => {
    if ((dirtyFields.customItems ?? []).some(Boolean)) {
      return;
    }
    const items = (insuranceExpense?.items ?? []).map(
      (item): InsuranceExpenseCustomItem => ({
        expenseItemID: item.guid,
        type: item.insuranceType,
        amount: item.amount.value.toString(),
        institution: item.institution ?? "",
        belongsTo: item.clientGuid ?? "",
        period: item.amount.frequency,
      }),
    );
    replace(items.length > 0 ? items : [{ ...emptyCustomItem }]);
  }, [insuranceExpense?.items, replace, dirtyFields.customItems]);

  const buildInsuranceExpenseItemBody = (field: InsuranceExpenseCustomItem): InsuranceExpenseItemBody => ({
    name: insuranceTypeOptions.find((o) => o.value === field.type)?.name || "",
    insuranceType: (field.type as InsuranceExpenseItemInsuranceType) || InsuranceExpenseItemInsuranceType.life_insurance,
    amount: {
      value: Number.parseFloat(field.amount ?? "0") || 0,
      currencyCode: "CZK",
      frequency: (field.period as AmountFrequency) || AmountFrequency.monthly,
    },
    institution: field.institution ?? "",
    clientGuid: field.belongsTo || undefined,
  });

  const invalidateInsuranceExpenseQueries = () => {
    queryClient.invalidateQueries({ queryKey: getGetInsuranceExpenseQueryKey(analysisId, versionId) });
    queryClient.invalidateQueries({ queryKey: getGetBudgetOverviewQueryKey(analysisId, versionId) });
  };

  const onSubmit = async (data: InsuranceExpenseFormType) => {
    try {
      const currentItems = detailed ? (data.customItems ?? []) : [];
      const currentGuids = new Set(currentItems.filter((item) => item.expenseItemID).map((item) => item.expenseItemID as string));

      const toCreate = currentItems.filter((item) => !item.expenseItemID);
      const toUpdate = currentItems.filter((item) => item.expenseItemID);
      const toDelete = (insuranceExpense?.items ?? []).filter((item) => !currentGuids.has(item.guid));

      await Promise.all([
        ...toCreate.map((item) => createInsuranceExpenseItemAsync({ analysisId, versionId, data: buildInsuranceExpenseItemBody(item) })),
        ...toUpdate.map((item) =>
          updateInsuranceExpenseItemAsync({
            analysisId,
            versionId,
            itemId: item.expenseItemID as string,
            data: buildInsuranceExpenseItemBody(item),
          }),
        ),
        ...toDelete.map((item) => deleteInsuranceExpenseItemAsync({ analysisId, versionId, itemId: item.guid })),
      ]);

      await updateInsuranceExpenseSettingsAsync({
        analysisId,
        versionId,
        data: {
          entryMode: detailed ? "individual" : "aggregate",
          aggregatedAmount: {
            value: Number.parseFloat(data.aggregatedAmount ?? "0") || 0,
            currencyCode: "CZK",
            frequency: AmountFrequency.monthly,
          },
        },
      });

      invalidateInsuranceExpenseQueries();
      onClose();
    } catch (_error) {
      toast.error(t(Resources.AnalysisDetail.Budget.Expenses.Form.Errors.ExpenseSubmitError));
    }
  };

  const onDeleteBulkExpense = async () => {
    try {
      for (const field of customFields) {
        if (!field.expenseItemID) continue;
        await deleteInsuranceExpenseItemAsync({ analysisId, versionId, itemId: field.expenseItemID });
      }
      replace([{ ...emptyCustomItem }]);

      await updateInsuranceExpenseSettingsAsync({
        analysisId,
        versionId,
        data: {
          entryMode: detailed ? "individual" : "aggregate",
          aggregatedAmount: { value: 0, currencyCode: "CZK", frequency: AmountFrequency.monthly },
        },
      });

      setOpenDeleteBulkModal(false);
      invalidateInsuranceExpenseQueries();
      onClose();
    } catch (_error) {
      toast.error(t(Resources.AnalysisDetail.Budget.Expenses.Form.Errors.ExpenseSubmitError));
    }
  };

  const deleteExpenseItem = (expenseItem: InsuranceExpenseCustomItem) => {
    if (expenseItem.expenseItemID) {
      setOpenDeleteModal({
        expenseItemID: expenseItem.expenseItemID,
      });
      return;
    }
    remove(customFields.findIndex((item) => item.id === expenseItem.id));
  };

  const onDeleteExpenseItem = (expenseItemID: string) => {
    const isLastItem = customFields.length === 1;
    const remainingTotal = calculateMonthlyTotal(customFields.filter((item) => item.expenseItemID !== expenseItemID));

    deleteInsuranceExpenseItem(
      { analysisId, versionId, itemId: expenseItemID },
      {
        onSuccess: async () => {
          remove(customFields.findIndex((item) => item.expenseItemID === expenseItemID));
          setOpenDeleteModal(null);

          try {
            await updateInsuranceExpenseSettingsAsync({
              analysisId,
              versionId,
              data: {
                entryMode: "individual",
                aggregatedAmount: { value: remainingTotal, currencyCode: "CZK", frequency: AmountFrequency.monthly },
              },
            });
          } catch {
            toast.error(t(Resources.AnalysisDetail.Budget.Expenses.Form.Errors.ExpenseSubmitError));
          }

          invalidateInsuranceExpenseQueries();
          if (isLastItem) {
            onClose();
          }
        },
        onError: () => {
          toast.error(t(Resources.AnalysisDetail.Budget.Expenses.Form.Errors.ExpenseSubmitError));
        },
      },
    );
  };

  const handleChangeDetailed = (value: boolean) => {
    setDetailed(value);
    clearErrors();
    setValue("detailed", value);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={Spacing.XXL} pb={Spacing.XXL} sx={{ height: "100%" }}>
        <Box>
          <Typography fontWeight={600} variant="body1" mb={Spacing.XL}>
            {t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.Insurance)}
          </Typography>

          <Grid container spacing={Spacing.XL}>
            <Grid size={6}>
              <BlFormInput
                control={control}
                name="aggregatedAmount"
                label={t(Resources.AnalysisDetail.Budget.Expenses.Form.Fields.Aggregated)}
                errors={errors}
                disabled={detailed || readOnly}
                inputEndAdornment={
                  <InputAdornment position="end">{`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}</InputAdornment>
                }
                mask={[
                  {
                    mask: Number,
                    thousandsSeparator: " ",
                    scale: 2,
                    min: 0,
                  },
                ]}
                placeholder="0"
              />
            </Grid>

            <Grid size={6}>
              <Box display="flex" alignItems="flex-start" mt={5} height="100%">
                <FormControlLabel
                  control={<Checkbox checked={detailed} disabled={readOnly} onChange={(_, checked) => handleChangeDetailed(checked)} />}
                  label={t(Resources.AnalysisDetail.Budget.Expenses.Form.Fields.DetailedCheckbox)}
                />
              </Box>
            </Grid>

            {detailed && (
              <>
                {customFields.map((field, idx) => (
                  <Fragment key={field.id}>
                    <Grid size={12} container spacing={Spacing.L}>
                      <Grid size={10}>
                        <FormDropdown
                          control={control}
                          disabled={readOnly}
                          name={`customItems.${idx}.type`}
                          options={insuranceTypeOptions}
                          label={t(Resources.AnalysisDetail.Budget.Expenses.Form.Labels.Item)}
                          errors={errors}
                          labelEndAdornment={<StyledLabelAdornment />}
                        />
                      </Grid>

                      <Grid size={1} display="flex" alignItems="center" justifyContent="center" mt={Spacing.XL}>
                        <Button variant="text" disabled={readOnly} onClick={() => deleteExpenseItem(field)}>
                          <BinIcon />
                        </Button>
                      </Grid>

                      <Grid size={1} display="flex" alignItems="center" justifyContent="center" mt={Spacing.XL}>
                        <RotatingArrowButton onClick={() => toggle(field.id)} />
                      </Grid>

                      <Grid size={6}>
                        <BlFormInput
                          control={control}
                          disabled={readOnly}
                          name={`customItems.${idx}.amount`}
                          label={t(Resources.AnalysisDetail.Budget.Expenses.Form.Labels.Value)}
                          errors={errors}
                          labelEndAdornment={<StyledLabelAdornment />}
                          inputEndAdornment={<InputAdornment position="end">{`${t(Resources.Common.CZKSuffix)}`}</InputAdornment>}
                          mask={[
                            {
                              mask: Number,
                              thousandsSeparator: " ",
                              scale: 2,
                            },
                          ]}
                          placeholder="0"
                          onChange={recalculateTotal}
                        />
                      </Grid>

                      <Grid size={6}>
                        <FormDropdown
                          control={control}
                          disabled={readOnly}
                          name={`customItems.${idx}.period`}
                          options={frequencyOptions}
                          label={t(Resources.AnalysisDetail.Budget.Expenses.Form.Labels.Frequency)}
                          errors={errors}
                          hasValidationText={false}
                          onChange={recalculateTotal}
                        />
                      </Grid>

                      <Collapse in={!!expandedMap[field.id]} sx={{ width: "100%" }}>
                        <Grid size={12} container spacing={Spacing.L} alignItems="center">
                          <Grid size={6}>
                            <BlFormInput
                              control={control}
                              disabled={readOnly}
                              name={`customItems.${idx}.institution`}
                              label={t(Resources.AnalysisDetail.Budget.Expenses.Form.Labels.Institution)}
                              errors={errors}
                              placeholder={t(Resources.AnalysisDetail.Budget.Expenses.Form.Placehodlers.Name)}
                            />
                          </Grid>

                          <Grid size={6}>
                            <FormDropdown
                              control={control}
                              disabled={readOnly}
                              name={`customItems.${idx}.belongsTo`}
                              options={belongsToOptions}
                              label={t(Resources.AnalysisDetail.Budget.Expenses.Form.Labels.BelongsTo)}
                              errors={errors}
                              hasValidationText={false}
                            />
                          </Grid>
                        </Grid>
                      </Collapse>
                    </Grid>
                    <Divider style={{ width: "100%" }} />
                  </Fragment>
                ))}

                <Grid size={12}>
                  <Button
                    variant="text"
                    disabled={readOnly}
                    colorCustom={theme.colors.brandPrimary}
                    startAdornment={<AddIcon />}
                    onClick={() => append({ ...emptyCustomItem })}
                  >
                    {t(Resources.AnalysisDetail.Budget.Expenses.Form.Buttons.AddCustomItem)}
                  </Button>
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      </Stack>

      <DialogModal
        isOpen={!!openDeleteModal}
        onClose={() => setOpenDeleteModal(null)}
        title={t(Resources.AnalysisDetail.Budget.Expenses.Form.DeleteExpenseItem)}
        subtitle={t(Resources.AnalysisDetail.Budget.Expenses.Form.DeleteExpenseItemHint)}
        primaryAction={
          <Button disabled={readOnly} onClick={() => openDeleteModal && onDeleteExpenseItem(openDeleteModal.expenseItemID)}>
            {t(Resources.Common.Yes)}
          </Button>
        }
      />

      <DialogModal
        isOpen={openDeleteBulkModal}
        onClose={() => setOpenDeleteBulkModal(false)}
        title={t(Resources.AnalysisDetail.Budget.Expenses.Form.DeleteExpense)}
        subtitle={t(Resources.AnalysisDetail.Budget.Expenses.Form.DeleteExpenseHint)}
        primaryAction={
          <Button disabled={readOnly} onClick={onDeleteBulkExpense}>
            {t(Resources.Common.Yes)}
          </Button>
        }
      />
    </form>
  );
});
